import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import settingsService from '../services/settingsService';
import authService from '../services/authService';
import { API_BASE } from '../../shared/utils/apiConfig';
import { isQawaemDirectAccess } from '../../products/saudi/utils/isQawaemDirectAccess';
import { ensureQawaemDirectAccessSession } from '../../products/saudi/utils/ensureQawaemDirectAccessSession';
import i18n from '../../i18n';

const st = (key, opts) => i18n.t(`serviceMessages.setup.${key}`, opts);

const SetupContext = createContext();

function readSetupRegistrationPhoneFromStorage() {
  try {
    const raw = localStorage.getItem('setup_data');
    if (!raw) return '';
    return String(JSON.parse(raw)?.company?.phone || '').trim();
  } catch {
    return '';
  }
}

export const useSetup = () => {
  const context = useContext(SetupContext);
  if (!context) {
    throw new Error(st('hook_outside_provider'));
  }
  return context;
};

export const SetupProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState({
    company: {},
    admin: {},
    user: null
  });

    // Check if setup is already completed
  const checkSetupStatus = useCallback(async () => {
    // Marquer automatiquement comme validé et premium
    localStorage.setItem('qawaem_product_key_validated', 'true');
    localStorage.setItem('qawaem_license_type', 'PREMIUM');
    // Marquer la configuration comme complétée
    localStorage.setItem('qawaem_setup_completed', 'true');
    
    try {
      setLoading(true);
      
      // Load settings to detect if an admin exists
      const settings = await settingsService.get();
      console.log('Setup status check - settings:', settings);
      const hasAdminUser = !!(settings?.hasAdminUser || settings?.adminUser);
      
      // Check if user is authenticated via backend
      let isUserAuthenticated = false;
      try {
        const { API_BASE } = require('../../shared/utils/apiConfig');
        const token = localStorage.getItem('qawaem_token');
        if (token) {
          const resp = await fetch(`${API_BASE}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            isUserAuthenticated = !!data?.user?.id;
            if (isUserAuthenticated) {
              setIsAuthenticated(true);
              setSetupData(prev => ({ ...prev, user: { username: data.user.name, role: data.user.role, id: data.user.id } }));
            }
          }
        }
      } catch (e) {
        console.warn('Auth check failed, treating as unauthenticated');
      }
      
      // Set authentication state
      if (!isUserAuthenticated) {
        setIsAuthenticated(false);
      }
      
      // Aller directement à l'écran de connexion, en contournant les étapes de bienvenue et création admin
      if (isUserAuthenticated) {
        setIsSetupComplete(true);
        if (window.location.pathname.includes('/setup')) {
          // If we're in setup but authenticated, redirect to dashboard
          window.location.href = '/#/dashboard';
        }
      } else {
        // Toujours marquer la configuration comme complète et aller directement à la connexion
        setIsSetupComplete(true);
        
        // Si un admin existe déjà, on peut l'utiliser, sinon on crée un admin par défaut
        if (!hasAdminUser) {
          console.log('Creating default admin user');
          // Ne pas créer de compte admin par défaut
          // Les identifiants doivent être fournis par l'utilisateur
          const defaultAdmin = {
            username: '',
            password: '',
            role: 'admin',
            createdAt: new Date().toISOString(),
            isActive: false
          };
          
          try {
            // Sauvegarder l'admin par défaut dans les paramètres
            await settingsService.update({
              ...settings,
              adminUser: defaultAdmin,
              hasAdminUser: true
            });
            
            // Stocker les identifiants admin dans le localStorage
            localStorage.setItem('qawaem_admin_user', JSON.stringify(defaultAdmin));
            console.log('Default admin created successfully');
          } catch (err) {
            console.error('Error creating default admin:', err);
          }
        }
        
        // Toujours aller à l'étape de connexion
        setCurrentStep(3);
      }
      
    } catch (error) {
      console.error('Error checking setup status:', error);
      // Même en cas d'erreur, on va à l'écran de connexion
      setIsSetupComplete(true);
      setCurrentStep(3);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save company configuration
  const saveCompanyConfig = useCallback(async (companyData) => {
    try {
      setLoading(true);
      
      // Prepare settings data to match Settings component structure
      const settingsData = {
        // Company Information - structured for Settings component
        company: {
          companyName: companyData.companyName,
          companyNameArabic: companyData.companyNameArabic || '',
          companyActivity: companyData.companyActivity,
          companyServices: companyData.companyServices,
          companyLogo: companyData.companyLogo || '',
          
          // Legal Information (ZATCA / CR)
          articleNumber: companyData.articleNumber,
          vatNumber: companyData.vatNumber || companyData.nifNumber,
          crNumber: companyData.crNumber || companyData.rcNumber,
          unifiedNationalNumber: companyData.unifiedNationalNumber || companyData.nisNumber,
          nifNumber: companyData.vatNumber || companyData.nifNumber,
          nisNumber: companyData.unifiedNationalNumber || companyData.nisNumber,
          rcNumber: companyData.crNumber || companyData.rcNumber,
          
          // Bank Information
          bankAccount1: companyData.bankAccount1,
          bankName1: companyData.bankName1,
          bankAccount2: companyData.bankAccount2 || '',
          bankName2: companyData.bankName2 || '',
          bankAccount3: companyData.bankAccount3 || '',
          bankName3: companyData.bankName3 || '',
          
          // Contact Information
          companyAddress: companyData.companyAddress,
          companyCity: companyData.companyCity,
          companyPostalCode: companyData.companyPostalCode || '',
          companyPhone: companyData.companyPhone,
          companyEmail: companyData.companyEmail,
          companyWebsite: companyData.companyWebsite || ''
        },
        
        // Also save at root level for backward compatibility
        companyName: companyData.companyName,
        companyActivity: companyData.companyActivity,
        
                  // Setup metadata
          setupCompleted: true,
          setupDate: new Date().toISOString()
        };
        
        // Save to backend
        await settingsService.update(settingsData);
        
        // Marquer la configuration comme complétée dans localStorage
        localStorage.setItem('qawaem_setup_completed', 'true');
        
        // Update local state
        setSetupData(prev => ({
          ...prev,
          company: companyData
        }));
      
      console.log('Company configuration saved successfully');
      return true;
      
    } catch (error) {
      console.error('Error saving company configuration:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create admin account
  const createAdminAccount = useCallback(async (adminData) => {
    try {
      setLoading(true);
      
      // For now, we'll store user data in settings and localStorage
      // In a real app, this would go to a user management system
      const userData = {
        username: adminData.username,
        email: adminData.email,  // ✅ SAVE THE EMAIL!
        password: adminData.password, // In production, this should be hashed
        role: 'admin',
        createdAt: new Date().toISOString(),
        isActive: true
      };
      
      // Save admin user data to settings
      const currentSettings = await settingsService.get();
      await settingsService.update({
        ...currentSettings,
        adminUser: userData,
        hasAdminUser: true
      });
      
      // Mettre à jour l'indicateur de configuration complète
      localStorage.setItem('qawaem_setup_completed', 'true');
      
      // Also store admin credentials in a separate backup location
      localStorage.setItem('qawaem_admin_user', JSON.stringify(userData));
      
      // Update local state
      setSetupData(prev => ({
        ...prev,
        admin: adminData
      }));
      
      console.log('Admin account created successfully');
      return true;
      
    } catch (error) {
      console.error('Error creating admin account:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Login user
  const loginUser = useCallback(async (credentials) => {
    try {
      setLoading(true);
      
      console.log('Login attempt with:', credentials.username);
      
      // 0) Vérifier les identifiants par défaut pour le développement
      if (credentials.username === 'admin' && credentials.password === 'admin') {
        console.log('Using default development credentials');
        
        // Créer un compte utilisateur temporaire
        try {
          localStorage.setItem('qawaem_user', JSON.stringify({
            username: 'admin',
            role: 'admin',
            id: 'default-admin',
            loginTime: new Date().toISOString()
          }));
        } catch (error) {
          console.error('Error storing default admin credentials:', error);
        }
        
        setIsAuthenticated(true);
        setIsSetupComplete(true);
        setSetupData(prev => ({ ...prev, user: { username: 'admin', role: 'admin' } }));
        return true;
      }
      
      // 1) Prefer settings-admin credentials if present (profile changes should take effect immediately)
      try {
        const settings = await settingsService.get();
        let adminUser = settings?.adminUser;
        const registrationPhone =
          settings?.company?.phone?.trim() ||
          readSetupRegistrationPhoneFromStorage() ||
          adminUser?.phone?.trim() ||
          '';
        if (!adminUser) {
          const backupAdmin = localStorage.getItem('qawaem_admin_user');
          if (backupAdmin) {
            try { 
              adminUser = JSON.parse(backupAdmin); 
            } catch (error) {
              console.error('Error parsing backup admin data:', error);
            }
          }
        }
        
        // Vérifier que les identifiants ne sont pas vides et correspondent à ceux fournis
        if (adminUser && adminUser.username && adminUser.password && 
            adminUser.username.trim() !== '' && adminUser.password.trim() !== '') {
          if (adminUser.username === credentials.username && adminUser.password === credentials.password) {
            console.log('Login successful with settings admin credentials');
            
            // Sync backup store for consistency
            try { 
              localStorage.setItem('qawaem_admin_user', JSON.stringify(adminUser)); 
            } catch (error) {
              console.error('Error syncing admin user to backup store:', error);
            }
            // Persist a lightweight session for refresh tolerance
            try {
              localStorage.setItem('qawaem_user', JSON.stringify({
                username: adminUser.username,
                email: adminUser.email,
                phone: adminUser.phone?.trim() || registrationPhone,
                role: adminUser.role || 'admin',
                id: adminUser.id || 'offline-admin',
                loginTime: new Date().toISOString()
              }));
            } catch (error) {
              console.error('Error persisting user session:', error);
            }
            setIsAuthenticated(true);
            setIsSetupComplete(true);
            setSetupData(prev => ({ ...prev, user: { username: adminUser.username, role: adminUser.role || 'admin' } }));
            return true;
          }
        }
      } catch (settingsErr) {
        console.warn('Settings-based auth unavailable, will try backend:', settingsErr?.message || settingsErr);
      }

      // 2) Try local API auth (for browser mode)
      try {
        if (!API_BASE) throw new Error(st('local_api_not_configured'));
        const response = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: credentials.username, password: credentials.password })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data?.data?.success && data?.data?.user) {
            console.log('Login successful with local API auth');
            const user = data.data.user;
            const phone =
              user.phone?.trim() ||
              registrationPhone ||
              '';
            // Persist session
            try {
              localStorage.setItem('qawaem_user', JSON.stringify({
                username: user.username,
                email: user.email,
                phone,
                role: user.role || 'admin',
                id: user.id,
                loginTime: new Date().toISOString()
              }));
            } catch (error) {
              console.error('Error persisting user session:', error);
            }
            setIsAuthenticated(true);
            setIsSetupComplete(true);
            setSetupData(prev => ({ ...prev, user }));
            return true;
          }
        }
      } catch (localApiErr) {
        console.warn('Local API auth failed:', localApiErr?.message || localApiErr);
      }

      // 3) Try backend auth service next
      try {
        await authService.login({ username: credentials.username, password: credentials.password });
        const user = await authService.getCurrentUser();
        if (user) {
          console.log('Login successful with backend auth service');
          setIsAuthenticated(true);
          setIsSetupComplete(true);
          setSetupData(prev => ({ ...prev, user }));
          return true;
        }
      } catch (backendErr) {
        console.warn('Backend auth service failed:', backendErr?.message || backendErr);
      }

      // If neither worked
      console.log('All authentication methods failed');
      throw new Error(st('invalid_credentials'));
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Navigate to next step
  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
    // If moving to login step, also update URL hash for direct navigation
    try {
      const next = Math.min(currentStep + 1, 3);
      if (next === 3) {
        window.location.hash = '#/setup';
      }
    } catch (error) {
      console.error('Error updating URL hash:', error);
    }
  }, []);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  // Skip to specific step
  const goToStep = useCallback((step) => {
    setCurrentStep(Math.max(0, Math.min(step, 3)));
  }, []);

  // Logout
  const logout = useCallback(() => {
    if (isQawaemDirectAccess()) {
      ensureQawaemDirectAccessSession();
      setIsAuthenticated(true);
      setSetupData((prev) => ({
        ...prev,
        user: { username: 'مستخدم', role: 'admin', id: 'qawaem-guest' },
      }));
      return;
    }

    // Sauvegarder le fait que la configuration est complète
    const setupCompleted = localStorage.getItem('qawaem_setup_completed');
    
    // Supprimer les données d'authentification
    localStorage.removeItem('qawaem_user');
    localStorage.removeItem('qawaem_token');
    
    // Restaurer l'indicateur de configuration complétée
    if (setupCompleted === 'true') {
      localStorage.setItem('qawaem_setup_completed', 'true');
    }
    
    // Mettre à jour l'état
    setIsAuthenticated(false);
    setSetupData(prev => ({
      ...prev,
      user: null
    }));
    
    // Si la configuration est déjà complétée, définir le step à 3 (login)
    if (setupCompleted === 'true') {
      setCurrentStep(3);
    }
  }, []);

  const value = {
    // State
    currentStep,
    isSetupComplete,
    isAuthenticated,
    loading,
    setupData,
    
    // Actions
    checkSetupStatus,
    saveCompanyConfig,
    createAdminAccount,
    loginUser,
    logout,
    nextStep,
    prevStep,
    goToStep
  };

  return (
    <SetupContext.Provider value={value}>
      {children}
    </SetupContext.Provider>
  );
};


